import { Command } from 'cmdk';
import type { Variants } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import type { KeyboardEventHandler } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  DynamicEntityCommands,
  useRegisterEntityCommands,
} from 'components/commandPalette/DynamicEntityCommands';
import {
  NavigationCommands,
  useRegisterNavigationCommands,
} from 'components/commandPalette/NavigationCommands';
import {
  SettingsCommands,
  useRegisterSettingsCommands,
} from 'components/commandPalette/SettingsCommands';
import { CommandGroup } from 'components/commandPalette/base/CommandGroup';
import { CommandView } from 'components/commandPalette/base/CommandView';
import FlexContainer from 'components/lib/ui/FlexContainer';
import KeyboardShortcut from 'components/lib/ui/KeyboardShortcut';

import useEventCallback from 'common/lib/hooks/useEventCallback';
import { color, fontSize, radius, spacing, variables } from 'common/lib/theme/dynamic';
import { useActiveCommandView, useCommandRegistry } from 'lib/commands/context';
import { useKeyboardShortcuts } from 'lib/keyboardShortcuts';

import { DEFAULT_TRANSITION } from 'constants/animation';

const MAX_WIDTH_PX = 564;
const OVERLAY_ANIMATION_VARIANTS: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};
const COMMAND_PALETTE_ANIMATION_VARIANTS: Variants = {
  initial: { scale: 0.97, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
};
export const COMMAND_PALETTE_COMMAND_ID = 'toggle-command-palette';

/**
 * CommandPalette component that renders the UI.
 */
export const CommandPalette: React.FC = () => {
  const [search, setSearch] = useState('');
  const { isOpen, toggleCommandPalette, registerCommand, getCommands } = useCommandRegistry();
  const listRef = useRef<HTMLDivElement>(null);
  const { activeCommandView, closeCommandView } = useActiveCommandView();

  // Initialize sequence-based keyboard shortcuts system.
  // Disable shortcuts when the palette is open.
  useKeyboardShortcuts({ isEnabled: !isOpen });

  // Register the command palette itself once
  useEffect(() => {
    registerCommand({
      id: COMMAND_PALETTE_COMMAND_ID,
      label: 'Toggle command palette',
      action: toggleCommandPalette,
      keys: ['command+k', 'ctrl+k'],
      analyticsMetadata: {
        analyticsId: COMMAND_PALETTE_COMMAND_ID,
      },
    });
  }, [toggleCommandPalette, registerCommand]);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useEventCallback((e) => {
    if (e.key === 'Escape' || (e.key === 'k' && (e.metaKey || e.ctrlKey))) {
      e.preventDefault();
      toggleCommandPalette();
    }

    if (e.key === 'Escape' && activeCommandView) {
      e.preventDefault();
      closeCommandView();
    }
  });

  // Register commands so they're available throughout the app, without the need for the palette to be open.
  useRegisterNavigationCommands();
  useRegisterSettingsCommands();
  const { isLoading: isLoadingEntityCommands } = useRegisterEntityCommands(search);

  useEffect(() => {
    // Clear the search when the palette is opened
    if (isOpen) {
      setSearch('');
    }

    listRef.current?.scrollTo({ top: 0 });
  }, [isOpen]);

  const contextualActions = getCommands('actions');

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Overlay
          $isOpen={isOpen}
          onClick={(e) => {
            e.stopPropagation();
            toggleCommandPalette();
          }}
        >
          <motion.div
            variants={COMMAND_PALETTE_ANIMATION_VARIANTS}
            initial="initial"
            animate="animate"
            exit="initial"
            transition={DEFAULT_TRANSITION}
          >
            <StyledCommand
              // Prevents closing the command palette when clicking anywhere
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              loop
            >
              {activeCommandView ? (
                <CommandView />
              ) : (
                <motion.div>
                  <SearchContainer alignCenter>
                    <Input
                      placeholder="Search or type a command..."
                      value={search}
                      onValueChange={setSearch}
                      autoFocus
                    />
                    <KeyboardShortcut keys={['mod+k']} />
                  </SearchContainer>
                  <List ref={listRef}>
                    {!isLoadingEntityCommands && <Empty>No results found</Empty>}

                    {/* Contextual actions should be rendered first, if present */}
                    {contextualActions.length > 0 && (
                      <CommandGroup.Root group="actions">
                        {contextualActions.map((command) => (
                          <CommandGroup.Item key={command.id} {...command} />
                        ))}
                      </CommandGroup.Root>
                    )}

                    {/* Render command groups */}
                    <NavigationCommands />
                    <SettingsCommands />
                    <DynamicEntityCommands />
                  </List>
                </motion.div>
              )}
            </StyledCommand>
          </motion.div>
        </Overlay>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const Overlay = styled(motion.div).attrs({
  variants: OVERLAY_ANIMATION_VARIANTS,
  transition: DEFAULT_TRANSITION,
  initial: 'initial',
  animate: 'animate',
  exit: 'initial',
})<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
  background: ${variables.color.background.modalOverlay};
`;

const StyledCommand = styled(Command)`
  width: ${MAX_WIDTH_PX}px;
  background: ${color.white};
  border-radius: ${radius.medium};
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
`;

const SearchContainer = styled(FlexContainer)`
  padding: ${spacing.default};
  border-bottom: 1px solid ${variables.color.divider.primary};
`;

const Input = styled(Command.Input)`
  border: none;
  background: none;
  flex-grow: 1;
  font-size: ${fontSize.large};
  color: ${color.text};

  ::placeholder {
    color: ${color.textLight};
  }

  :focus {
    outline: none;
  }
`;

const List = styled(Command.List)`
  --scrollbar-color: ${color.gray8};

  overflow: auto;
  max-height: 50vh;
  padding: ${spacing.xxsmall};
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-color) transparent;
`;

const Empty = styled(Command.Empty)`
  padding: ${spacing.small};
  color: ${color.textLight};
  text-align: center;
`;
